import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const services = [
  {
    title: 'Frameless Shower Doors',
    titleEs: 'Puertas de Ducha Sin Marco',
    desc: 'Sleek, modern frameless glass enclosures that transform any bathroom into a luxury spa experience.',
    descEs: 'Cerramientos de vidrio sin marco modernos que transforman cualquier baño en un spa de lujo.',
    img: 'https://public.readdy.ai/ai/img_res/4ac2f0b6324796010db8306727643460.jpg',
    span: 'col-span-1 row-span-2',
    height: 'h-full min-h-[480px]',
  },
  {
    title: 'Glass Railings',
    titleEs: 'Barandales de Vidrio',
    desc: 'Frameless glass railings for stairs, balconies, and decks — safety meets sophistication.',
    descEs: 'Barandales de vidrio sin marco para escaleras, balcones y terrazas.',
    img: 'https://readdy.ai/api/search-image?query=modern%20frameless%20glass%20stair%20railing%20in%20luxury%20home%20interior%2C%20clear%20tempered%20glass%20panels%20with%20stainless%20steel%20posts%2C%20open%20concept%20living%20space%2C%20professional%20interior%20design%20photo&width=700&height=500&seq=svc004&orientation=landscape',
    span: 'col-span-1',
    height: 'h-56',
  },
  {
    title: 'Custom Mirrors',
    titleEs: 'Espejos Personalizados',
    desc: 'Bespoke mirrors cut to any size and shape — perfect for bathrooms, gyms, and feature walls.',
    descEs: 'Espejos a medida para baños, gimnasios y paredes decorativas.',
    img: 'https://readdy.ai/api/search-image?query=large%20custom%20frameless%20mirror%20in%20modern%20bathroom%20with%20LED%20lighting%2C%20clean%20white%20walls%2C%20minimalist%20design%2C%20luxury%20interior%2C%20professional%20photography&width=700&height=500&seq=svc005&orientation=landscape',
    span: 'col-span-1',
    height: 'h-56',
  },
  {
    title: 'Tub Enclosures',
    titleEs: 'Cerramientos de Bañera',
    desc: 'Custom glass enclosures for bathtubs — elegant, watertight, and easy to clean.',
    descEs: 'Cerramientos de vidrio personalizados para bañeras — elegantes y fáciles de limpiar.',
    img: 'https://readdy.ai/api/search-image?query=luxury%20glass%20bathtub%20enclosure%20with%20clear%20tempered%20glass%20panels%2C%20freestanding%20tub%2C%20marble%20floor%2C%20modern%20bathroom%20interior%2C%20professional%20architectural%20photography&width=700&height=500&seq=svc003&orientation=landscape',
    span: 'col-span-1',
    height: 'h-56',
  },
  {
    title: 'Sliding Glass Doors',
    titleEs: 'Puertas Corredizas',
    desc: 'Space-saving sliding glass doors with smooth, whisper-quiet glide systems.',
    descEs: 'Puertas corredizas de vidrio que ahorran espacio con sistemas de deslizamiento silencioso.',
    img: 'https://readdy.ai/api/search-image?query=modern%20sliding%20glass%20shower%20door%20with%20brushed%20nickel%20hardware%2C%20clear%20glass%20panels%2C%20contemporary%20bathroom%20design%2C%20clean%20white%20background%2C%20professional%20photo&width=700&height=500&seq=svc002&orientation=landscape',
    span: 'col-span-1',
    height: 'h-56',
  },
  {
    title: 'Glass Partitions',
    titleEs: 'Particiones de Vidrio',
    desc: 'Office and home glass partitions that create open, light-filled spaces without sacrificing privacy.',
    descEs: 'Particiones de vidrio para oficinas y hogares que crean espacios abiertos y luminosos.',
    img: 'https://readdy.ai/api/search-image?query=modern%20glass%20office%20partition%20wall%20with%20frosted%20glass%20panels%2C%20contemporary%20workspace%20design%2C%20natural%20light%2C%20minimalist%20interior%2C%20professional%20architectural%20photo&width=700&height=500&seq=svc006&orientation=landscape',
    span: 'col-span-1',
    height: 'h-56',
  },
];

const ServicesOverview = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { i18n } = useTranslation();
  const isEs = i18n.resolvedLanguage === 'es';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-card').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 md:py-28 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 gap-6">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3 block">
              {isEs ? 'Lo Que Hacemos' : 'What We Do'}
            </span>
            <h2 className="text-4xl md:text-5xl font-light text-black leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {isEs ? 'Nuestros Servicios de Vidrio' : 'Our Glass Services'}
            </h2>
          </div>
          <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
            {isEs
              ? 'Desde puertas de ducha sin marco hasta espejos personalizados y barandales de vidrio — manejamos cada instalación con precisión.'
              : 'From frameless shower doors to custom mirrors and glass railings — every installation handled with precision and care.'}
          </p>
        </div>

        {/* Masonry-style Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
          {services.map((svc, idx) => (
            <div
              key={svc.title}
              className={`reveal-card opacity-0 translate-y-10 transition-all duration-700 ease-out group cursor-pointer ${idx === 0 ? 'sm:row-span-2' : ''}`}
            >
              <Link to="/services" className="block h-full">
                <div className={`relative overflow-hidden rounded-2xl ${idx === 0 ? 'h-full min-h-[480px]' : 'h-64'}`}>
                  {/* Image with Ken Burns */}
                  <img
                    src={svc.img}
                    alt={isEs ? svc.titleEs : svc.title}
                    className="w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />

                  {/* Base gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  {/* Glass shine sweep on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none overflow-hidden rounded-2xl">
                    <div className="absolute -inset-full top-0 h-full w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12 group-hover:translate-x-[400%] transition-transform duration-1000 ease-out" />
                  </div>

                  {/* Dark overlay on hover */}
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-400" />

                  {/* Bottom content */}
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="text-white font-semibold text-lg leading-tight mb-1">
                      {isEs ? svc.titleEs : svc.title}
                    </h3>
                    <p className="text-white/70 text-xs leading-relaxed max-w-xs opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                      {isEs ? svc.descEs : svc.desc}
                    </p>
                    {/* Slide-up button */}
                    <div className="mt-3 translate-y-6 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400 delay-75">
                      <span className="inline-flex items-center gap-2 bg-white text-black text-xs font-semibold px-4 py-2 rounded-full whitespace-nowrap">
                        {isEs ? 'Ver Servicio' : 'View Service'} <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link
            to="/services"
            className="inline-flex items-center gap-3 bg-black text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer group"
          >
            {isEs ? 'Ver Todos los Servicios' : 'View All Services'}
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesOverview;
